import { Link } from "react-router-dom";
import { ArrowRight, Telescope, Radar, Image as ImageIcon, Globe2, Orbit, Rocket, Sparkles } from "lucide-react";
import AdSlot from "@/components/AdSlot";
import biLogo from "@/assets/bronto-intelligence-logo-transparent.png";

const platforms = [
  {
    eyebrow: "Real cosmic data",
    title: "Picture of the Day",
    body: "Our daily astronomy image with an explanation from professional astronomers.",
    to: "/apod",
    icon: Sparkles,
  },
  {
    eyebrow: "Robotic exploration",
    title: "Mars Rover Photos",
    body: "Latest images from Curiosity, Perseverance, Opportunity, and Spirit on the Martian surface.",
    to: "/mars-rover",
    icon: Rocket,
  },
  {
    eyebrow: "Our planet",
    title: "Earth From Space",
    body: "Full-disk Earth imagery captured a million miles away from our deep-space vantage point.",
    to: "/earth",
    icon: Globe2,
  },
  {
    eyebrow: "Planetary defense",
    title: "Asteroid Tracker",
    body: "Near-Earth objects passing by today — size, speed, distance, and hazard assessment.",
    to: "/asteroids",
    icon: Radar,
  },
  {
    eyebrow: "Visual archive",
    title: "Space Gallery News",
    body: "The latest light from the world's most powerful telescopes — Webb, Hubble, and the deep cosmic archive.",
    to: "/gallery",
    icon: ImageIcon,
  },
  {
    eyebrow: "Solar System",
    title: "Planet Facts",
    body: "Distance, moons, atmosphere, and the strange facts that make each of the eight planets unique.",
    to: "/planets",
    icon: Orbit,
  },
];

const focuses = [
  { n: "01", title: "Astronomy", body: "Bringing daily astronomical observation into a clean, modern reading experience." },
  { n: "02", title: "Planetary Science", body: "Following robotic missions, surface imagery, and the latest from Mars and beyond." },
  { n: "03", title: "Earth Observation", body: "Visualizing our planet through scientific instruments stationed in deep space." },
  { n: "04", title: "Near-Earth Objects", body: "Tracking the asteroids and comets that cross Earth's orbital neighborhood." },
  { n: "05", title: "Visual Archives", body: "Surfacing the most significant imagery from decades of space exploration." },
  { n: "06", title: "Public Discovery", body: "Making real cosmic data accessible, beautiful, and easy to explore for everyone." },
];

export default function Home() {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 md:pt-28 pb-20 md:pb-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="eyebrow-row fade-in-up">
            <span className="eyebrow inline-flex items-center gap-2 flex-wrap">
              A discovery platform by
              <img
                src={biLogo}
                alt="Bronto Intelligence"
                className="h-4 md:h-5 w-auto inline-block align-middle"
                data-testid="hero-eyebrow-bi-logo"
              />
            </span>
          </div>
          <h1 className="display mt-6 text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl fade-in-up delay-1" data-testid="home-hero-title">
            Discover the <span className="rainbow-text">universe</span>,
            <br />one image at a time.
          </h1>
          <p className="mt-7 text-base md:text-xl text-white/65 max-w-2xl leading-relaxed fade-in-up delay-2">
            Bronto Cosmos brings together real space data — astronomy images, Mars rover photographs,
            Earth observation, and near-Earth asteroid tracking — into a single, beautifully designed discovery experience.
          </p>
          <div className="mt-10 fade-in-up delay-3 flex flex-col items-start gap-3">
            <Link to="/today" className="btn-pill btn-primary" data-testid="hero-cta-today">
              Explore Today&apos;s Space
              <ArrowRight size={16} />
            </Link>
            <div className="flex flex-wrap gap-3">
              <Link to="/gallery" className="btn-pill btn-secondary" data-testid="hero-cta-gallery">
                Space Gallery News
              </Link>
              <Link to="/asteroids" className="btn-pill btn-secondary" data-testid="hero-cta-asteroids">
                View Asteroids
              </Link>
              <Link to="/apod" className="btn-pill btn-secondary" data-testid="hero-cta-apod">
                Picture of the Day
              </Link>
            </div>
          </div>

          {/* decorative orbit rings */}
          <div className="pointer-events-none absolute right-[-220px] top-24 hidden md:block">
            <div className="relative w-[640px] h-[640px]">
              <div className="orbit-ring" style={{ inset: 0 }} />
              <div className="orbit-ring" style={{ inset: 60, animationDuration: "90s" }} />
              <div className="orbit-ring" style={{ inset: 130, animationDuration: "120s" }} />
              <div className="absolute" style={{ top: "20%", right: "16%" }}>
                <div className="planet-sphere" style={{ width: 110, height: 110, background: "radial-gradient(circle at 30% 30%, #6fb8ff, #1d62b6 55%, #082147 100%)", "--glow": "rgba(80,160,255,0.45)" }} />
              </div>
              <div className="absolute" style={{ bottom: "10%", left: "14%" }}>
                <div className="planet-sphere" style={{ width: 70, height: 70, background: "radial-gradient(circle at 30% 30%, #ff9874, #c0432a 55%, #4a1409 100%)", "--glow": "rgba(255,110,80,0.40)" }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <AdSlot id="below-hero" className="max-w-7xl mx-auto px-6 mb-12" />

      {/* What we are */}
      <section className="section">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="eyebrow-row">
              <span className="eyebrow">Who we are</span>
            </div>
            <h2 className="display mt-5 text-4xl md:text-5xl lg:text-6xl">
              A space discovery <span className="rainbow-text">platform</span>.
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 text-white/70 leading-relaxed space-y-5 text-base md:text-lg">
            <p>
              Bronto Cosmos is a science, astronomy, and space discovery platform by Bronto Intelligence Pvt. Ltd.
              The platform brings together open data from leading space agencies and presents it through a calm,
              high-fidelity interface designed for reading, browsing, and exploring.
            </p>
            <p>
              The platform draws on real cosmic observation data — daily astronomy imagery, planetary mission photographs,
              Earth observation feeds, the near-Earth object stream, and a deep visual archive — and
              presents the results as a single, coherent discovery experience.
            </p>
            <Link to="/about" className="btn-pill btn-secondary mt-3">
              Learn more about us <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Platforms / discover grid */}
      <section className="section">
        <div className="max-w-7xl mx-auto px-6 mb-12 md:mb-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="eyebrow-row">
                <span className="eyebrow">Discover</span>
              </div>
              <h2 className="display mt-5 text-4xl md:text-5xl lg:text-6xl">
                Six ways to explore <span className="rainbow-text">space</span>.
              </h2>
            </div>
            <Link to="/today" className="btn-pill btn-secondary" data-testid="discover-cta">
              Open Space Today <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
          {platforms.map((p) => {
            const Icon = p.icon;
            return (
              <Link
                key={p.to}
                to={p.to}
                data-testid={`platform-card-${p.to.replace("/", "")}`}
                className="glass hover-lift p-7 md:p-8 flex flex-col gap-5 group"
              >
                <div className="flex items-center justify-between">
                  <div className="eyebrow">{p.eyebrow}</div>
                  <div className="w-9 h-9 rounded-full border border-white/10 bg-white/5 flex items-center justify-center text-white/70 group-hover:text-white transition-colors">
                    <Icon size={16} />
                  </div>
                </div>
                <h3 className="display text-2xl md:text-3xl tracking-tight">{p.title}</h3>
                <p className="text-sm md:text-[15px] text-white/60 leading-relaxed">{p.body}</p>
                <div className="mt-auto pt-3 inline-flex items-center gap-2 text-sm text-white/70 group-hover:text-white transition-colors">
                  Explore <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <AdSlot id="mid" className="max-w-7xl mx-auto px-6" />

      {/* Focus areas */}
      <section className="section">
        <div className="max-w-7xl mx-auto px-6 mb-12 md:mb-16">
          <div className="eyebrow-row">
            <span className="eyebrow">Focus areas</span>
          </div>
          <h2 className="display mt-5 text-4xl md:text-5xl lg:text-6xl max-w-3xl">
            Built around real <span className="rainbow-text">scientific data</span>.
          </h2>
        </div>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {focuses.map((f) => (
            <div key={f.n} className="glass p-7 md:p-8">
              <div className="text-xs text-white/35 font-mono">{f.n}</div>
              <h3 className="display mt-4 text-xl md:text-2xl">{f.title}</h3>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="glass-strong p-10 md:p-16 relative overflow-hidden">
            <div className="orbit-ring hidden md:block" style={{ inset: -200, width: 600, height: 600 }} />
            <div className="eyebrow-row relative">
              <span className="eyebrow">Start exploring</span>
            </div>
            <h2 className="display mt-5 text-4xl md:text-6xl lg:text-7xl max-w-3xl relative">
              The more you know, <br />
              the more <span className="rainbow-text">magical</span> it becomes.
            </h2>
            <div className="mt-8 flex flex-wrap gap-3 relative">
              <Link to="/today" className="btn-pill btn-primary">
                Open Space Today <ArrowRight size={16} />
              </Link>
              <Link to="/gallery" className="btn-pill btn-secondary">
                Browse the Gallery
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
